import * as React from 'react';
import { IgcGridComponent as Component } from 'igniteui-webcomponents-grids/grids/index.js';
import type { IgcGridComponentEventMap } from 'igniteui-webcomponents-grids/grids/index.js';
import { type EventName, createComponent } from '../react-props.js';

/**
 * Grid provides a way to present and manipulate tabular data.
 * @fires dataPreLoad - Emitted when a new chunk of data is loaded from virtualization.
 * @fires groupingExpressionsChange - Emitted when grouping is performed.
 * @fires groupingExpansionStateChange - Emitted when groups are expanded/collapsed.
 * @fires groupingDone - Emitted when columns are grouped/ungrouped.
 * @fires filteringExpressionsTreeChange - Emitted after filtering is performed.
 * @fires advancedFilteringExpressionsTreeChange - Emitted after advanced filtering is performed.
 * @fires gridScroll - Emitted when grid is scrolled horizontally/vertically.
 * @fires cellClick - Emitted when a cell is clicked.
 * @fires rowClick - Emitted when a row is clicked.
 * @fires formGroupCreated - Emitted when formGroup is created on edit of row/cell.
 * @fires validationStatusChange - Emitted when grid's validation status changes.
 * @fires selected - Emitted when a cell is selected.
 * @fires rowSelectionChanging - Emitted when `IgxGridRowComponent` is selected.
 * @fires columnSelectionChanging - Emitted when `IgxColumnComponent` is selected.
 * @fires columnPin - Emitted before `IgxColumnComponent` is pinned.
 * @fires columnPinned - Emitted after `IgxColumnComponent` is pinned.
 * @fires cellEditEnter - Emitted when cell enters edit mode.
 * @fires cellEditExit - Emitted when cell exits edit mode.
 * @fires cellEdit - Emitted when cell has been edited.
 * @fires cellEditDone - Emitted after cell has been edited and editing has been committed.
 * @fires rowEditEnter - Emitted when a row enters edit mode.
 * @fires rowEdit - Emitted when exiting edit mode for a row.
 * @fires rowEditDone - Emitted after exiting edit mode for a row and editing has been committed.
 * @fires rowEditExit - Emitted when row editing is canceled.
 * @fires columnInit - Emitted when a column is initialized.
 * @fires columnsAutogenerated -
 * @fires sorting - Emitted before sorting expressions are applied.
 * @fires sortingDone - Emitted after sorting is completed.
 * @fires filtering - Emitted before filtering expressions are applied.
 * @fires filteringDone - Emitted after filtering is performed through the UI.
 * @fires rowAdded - Emitted when a row is added.
 * @fires rowDeleted - Emitted when a row is deleted.
 * @fires rowDelete - Emmited when deleting a row.
 * @fires rowAdd - Emmited just before the newly added row is commited.
 * @fires columnResized - Emitted after column is resized.
 * @fires contextMenu - Emitted when a cell or row is right clicked.
 * @fires doubleClick - Emitted when a cell is double clicked.
 * @fires columnVisibilityChanging - Emitted before column visibility is changed.
 * @fires columnVisibilityChanged - Emitted after column visibility is changed.
 * @fires columnMovingStart - Emitted when column moving starts.
 * @fires columnMoving - Emitted during the column moving operation.
 * @fires columnMovingEnd - Emitted when column moving ends.
 * @fires gridKeydown - Emitted when keydown is triggered over element inside grid's body.
 * @fires rowDragStart - Emitted when start dragging a row.
 * @fires rowDragEnd - Emitted when dropping a row.
 * @fires gridCopy - Emitted when a copy operation is executed.
 * @fires expansionStatesChange - Emitted when the rows are expanded or collapsed.
 * @fires selectedRowsChange - Emitted when the rows are selected or deselected.
 * @fires rowToggle - Emitted when the expanded state of a row gets changed.
 * @fires rowPinning - Emitted when the pinned state of a row is changed.
 * @fires rowPinned - Emitted when the pinned state of a row is changed.
 * @fires activeNodeChange - Emitted when the active node is changed.
 * @fires sortingExpressionsChange - Emitted before sorting is performed.
 * @fires toolbarExporting - Emitted when an export process is initiated by the user.
 * @fires rangeSelected - Emitted when making a range selection.
 * @fires rendered - Emitted after the ngAfterViewInit hook. At this point the grid exists in the DOM
 * @fires dataChanging - Emitted before the grid's data view is changed because of a data operation, rebinding, etc.
 * @fires dataChanged - Emitted after the grid's data view is changed because of a data operation, rebinding, etc.
 */
export const IgrGrid = createComponent({
  tagName: 'igc-grid',
  displayName: 'IgrGrid',
  elementClass: Component,
  react: React,
  moveBackOnDelete: true,
  events: {
    onDataPreLoad: 'dataPreLoad' as EventName<
      IgcGridComponentEventMap['dataPreLoad']
    >,
    onGroupingExpressionsChange: 'groupingExpressionsChange' as EventName<
      IgcGridComponentEventMap['groupingExpressionsChange']
    >,
    onGroupingExpansionStateChange: 'groupingExpansionStateChange' as EventName<
      IgcGridComponentEventMap['groupingExpansionStateChange']
    >,
    onGroupingDone: 'groupingDone' as EventName<
      IgcGridComponentEventMap['groupingDone']
    >,
    onFilteringExpressionsTreeChange:
      'filteringExpressionsTreeChange' as EventName<
        IgcGridComponentEventMap['filteringExpressionsTreeChange']
      >,
    onAdvancedFilteringExpressionsTreeChange:
      'advancedFilteringExpressionsTreeChange' as EventName<
        IgcGridComponentEventMap['advancedFilteringExpressionsTreeChange']
      >,
    onGridScroll: 'gridScroll' as EventName<
      IgcGridComponentEventMap['gridScroll']
    >,
    onCellClick: 'cellClick' as EventName<
      IgcGridComponentEventMap['cellClick']
    >,
    onRowClick: 'rowClick' as EventName<IgcGridComponentEventMap['rowClick']>,
    onFormGroupCreated: 'formGroupCreated' as EventName<
      IgcGridComponentEventMap['formGroupCreated']
    >,
    onValidationStatusChange: 'validationStatusChange' as EventName<
      IgcGridComponentEventMap['validationStatusChange']
    >,
    onSelected: 'selected' as EventName<IgcGridComponentEventMap['selected']>,
    onRowSelectionChanging: 'rowSelectionChanging' as EventName<
      IgcGridComponentEventMap['rowSelectionChanging']
    >,
    onColumnSelectionChanging: 'columnSelectionChanging' as EventName<
      IgcGridComponentEventMap['columnSelectionChanging']
    >,
    onColumnPin: 'columnPin' as EventName<
      IgcGridComponentEventMap['columnPin']
    >,
    onColumnPinned: 'columnPinned' as EventName<
      IgcGridComponentEventMap['columnPinned']
    >,
    onCellEditEnter: 'cellEditEnter' as EventName<
      IgcGridComponentEventMap['cellEditEnter']
    >,
    onCellEditExit: 'cellEditExit' as EventName<
      IgcGridComponentEventMap['cellEditExit']
    >,
    onCellEdit: 'cellEdit' as EventName<IgcGridComponentEventMap['cellEdit']>,
    onCellEditDone: 'cellEditDone' as EventName<
      IgcGridComponentEventMap['cellEditDone']
    >,
    onRowEditEnter: 'rowEditEnter' as EventName<
      IgcGridComponentEventMap['rowEditEnter']
    >,
    onRowEdit: 'rowEdit' as EventName<IgcGridComponentEventMap['rowEdit']>,
    onRowEditDone: 'rowEditDone' as EventName<
      IgcGridComponentEventMap['rowEditDone']
    >,
    onRowEditExit: 'rowEditExit' as EventName<
      IgcGridComponentEventMap['rowEditExit']
    >,
    onColumnInit: 'columnInit' as EventName<
      IgcGridComponentEventMap['columnInit']
    >,
    onColumnsAutogenerated: 'columnsAutogenerated' as EventName<
      IgcGridComponentEventMap['columnsAutogenerated']
    >,
    onSorting: 'sorting' as EventName<IgcGridComponentEventMap['sorting']>,
    onSortingDone: 'sortingDone' as EventName<
      IgcGridComponentEventMap['sortingDone']
    >,
    onFiltering: 'filtering' as EventName<
      IgcGridComponentEventMap['filtering']
    >,
    onFilteringDone: 'filteringDone' as EventName<
      IgcGridComponentEventMap['filteringDone']
    >,
    onRowAdded: 'rowAdded' as EventName<IgcGridComponentEventMap['rowAdded']>,
    onRowDeleted: 'rowDeleted' as EventName<
      IgcGridComponentEventMap['rowDeleted']
    >,
    onRowDelete: 'rowDelete' as EventName<
      IgcGridComponentEventMap['rowDelete']
    >,
    onRowAdd: 'rowAdd' as EventName<IgcGridComponentEventMap['rowAdd']>,
    onColumnResized: 'columnResized' as EventName<
      IgcGridComponentEventMap['columnResized']
    >,
    onContextMenu: 'contextMenu' as EventName<
      IgcGridComponentEventMap['contextMenu']
    >,
    onDoubleClick: 'doubleClick' as EventName<
      IgcGridComponentEventMap['doubleClick']
    >,
    onColumnVisibilityChanging: 'columnVisibilityChanging' as EventName<
      IgcGridComponentEventMap['columnVisibilityChanging']
    >,
    onColumnVisibilityChanged: 'columnVisibilityChanged' as EventName<
      IgcGridComponentEventMap['columnVisibilityChanged']
    >,
    onColumnMovingStart: 'columnMovingStart' as EventName<
      IgcGridComponentEventMap['columnMovingStart']
    >,
    onColumnMoving: 'columnMoving' as EventName<
      IgcGridComponentEventMap['columnMoving']
    >,
    onColumnMovingEnd: 'columnMovingEnd' as EventName<
      IgcGridComponentEventMap['columnMovingEnd']
    >,
    onGridKeydown: 'gridKeydown' as EventName<
      IgcGridComponentEventMap['gridKeydown']
    >,
    onRowDragStart: 'rowDragStart' as EventName<
      IgcGridComponentEventMap['rowDragStart']
    >,
    onRowDragEnd: 'rowDragEnd' as EventName<
      IgcGridComponentEventMap['rowDragEnd']
    >,
    onGridCopy: 'gridCopy' as EventName<IgcGridComponentEventMap['gridCopy']>,
    onExpansionStatesChange: 'expansionStatesChange' as EventName<
      IgcGridComponentEventMap['expansionStatesChange']
    >,
    onSelectedRowsChange: 'selectedRowsChange' as EventName<
      IgcGridComponentEventMap['selectedRowsChange']
    >,
    onRowToggle: 'rowToggle' as EventName<
      IgcGridComponentEventMap['rowToggle']
    >,
    onRowPinning: 'rowPinning' as EventName<
      IgcGridComponentEventMap['rowPinning']
    >,
    onRowPinned: 'rowPinned' as EventName<
      IgcGridComponentEventMap['rowPinned']
    >,
    onActiveNodeChange: 'activeNodeChange' as EventName<
      IgcGridComponentEventMap['activeNodeChange']
    >,
    onSortingExpressionsChange: 'sortingExpressionsChange' as EventName<
      IgcGridComponentEventMap['sortingExpressionsChange']
    >,
    onToolbarExporting: 'toolbarExporting' as EventName<
      IgcGridComponentEventMap['toolbarExporting']
    >,
    onRangeSelected: 'rangeSelected' as EventName<
      IgcGridComponentEventMap['rangeSelected']
    >,
    onRendered: 'rendered' as EventName<IgcGridComponentEventMap['rendered']>,
    onDataChanging: 'dataChanging' as EventName<
      IgcGridComponentEventMap['dataChanging']
    >,
    onDataChanged: 'dataChanged' as EventName<
      IgcGridComponentEventMap['dataChanged']
    >,
  },
  renderProps: {
    dropAreaTemplate: 'dropAreaTemplate',
    detailTemplate: 'detailTemplate',
    groupByRowSelectorTemplate: 'groupByRowSelectorTemplate',
    groupRowTemplate: 'groupRowTemplate',
    emptyGridTemplate: 'emptyGridTemplate',
    addRowEmptyTemplate: 'addRowEmptyTemplate',
    loadingGridTemplate: 'loadingGridTemplate',
    dragGhostCustomTemplate: 'dragGhostCustomTemplate',
    rowEditTextTemplate: 'rowEditTextTemplate',
    rowAddTextTemplate: 'rowAddTextTemplate',
    rowEditActionsTemplate: 'rowEditActionsTemplate',
    rowExpandedIndicatorTemplate: 'rowExpandedIndicatorTemplate',
    rowCollapsedIndicatorTemplate: 'rowCollapsedIndicatorTemplate',
    headerExpandedIndicatorTemplate: 'headerExpandedIndicatorTemplate',
    headerCollapsedIndicatorTemplate: 'headerCollapsedIndicatorTemplate',
    excelStyleHeaderIconTemplate: 'excelStyleHeaderIconTemplate',
    sortAscendingHeaderIconTemplate: 'sortAscendingHeaderIconTemplate',
    sortDescendingHeaderIconTemplate: 'sortDescendingHeaderIconTemplate',
    sortHeaderIconTemplate: 'sortHeaderIconTemplate',
    headSelectorTemplate: 'headSelectorTemplate',
    rowSelectorTemplate: 'rowSelectorTemplate',
    dragIndicatorIconTemplate: 'dragIndicatorIconTemplate',
  },
});

export type IgrGrid = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrGridModule = Component;
